import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ATTACHMENT_LIMIT, ATTACHMENT_MAX_BYTES, isAttachableFile } from '../enums/attachments';

const NONE = [];
const NOTICE_MS = 4000;
let sequence = 0;

// The images of the prompt being written (contract 2.7.0 proposal): the home's or one
// project chat's, kept per `scope` so a draft keeps its images while the creator looks
// elsewhere. Each one is uploaded as soon as it is added (`upload`: prepare → presigned
// PUT → confirm, as an `art_image`), previewed from the local file meanwhile, and the
// message goes only once every one is uploaded (`busy`).
export default function useAttachments({ scope, enabled, upload }) {
  const [lists, setLists] = useState({});
  const [notice, setNotice] = useState(null);
  const listsRef = useRef(lists);
  listsRef.current = lists;
  const noticeTimer = useRef(0);
  const items = lists[scope] || NONE;

  const patch = useCallback((key, id, change) => setLists(all => (all[key] ? { ...all, [key]: all[key].map(item => (item.id === id ? { ...item, ...change } : item)) } : all)), []);
  const say = useCallback((key, params = {}) => {
    clearTimeout(noticeTimer.current);
    setNotice({ key, params });
    noticeTimer.current = setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);
  // An image removed while it uploads is simply not found when the upload ends.
  const send = useCallback(async (key, item) => {
    patch(key, item.id, { status: 'uploading' });
    try { patch(key, item.id, { status: 'ready', asset: await upload(item.file) }); } catch { patch(key, item.id, { status: 'failed' }); }
  }, [patch, upload]);

  const add = useCallback(input => {
    const files = [...(input || [])];
    if (!enabled || !upload || !files.length) return;
    const key = scope;
    const room = ATTACHMENT_LIMIT - (listsRef.current[key] || []).length;
    const images = files.filter(isAttachableFile);
    const small = images.filter(file => file.size <= ATTACHMENT_MAX_BYTES);
    const accepted = small.slice(0, Math.max(0, room));
    if (images.length < files.length) say('attachType');
    else if (small.length < images.length) say('attachTooLarge');
    else if (accepted.length < small.length) say('attachLimit', { count: ATTACHMENT_LIMIT });
    if (!accepted.length) return;
    const added = accepted.map(file => ({ id: `att-${++sequence}`, file, name: file.name, preview: URL.createObjectURL(file), status: 'uploading', role: 'auto', asset: null }));
    setLists(all => ({ ...all, [key]: [...(all[key] || []), ...added] }));
    added.forEach(item => send(key, item));
  }, [enabled, upload, scope, say, send]);

  const remove = useCallback(id => setLists(all => {
    const list = all[scope] || [];
    list.filter(item => item.id === id).forEach(item => URL.revokeObjectURL(item.preview));
    return { ...all, [scope]: list.filter(item => item.id !== id) };
  }), [scope]);
  const retry = useCallback(id => { const item = (listsRef.current[scope] || []).find(entry => entry.id === id); if (item) send(scope, item); }, [scope, send]);
  const setRole = useCallback((id, role) => patch(scope, id, { role }), [patch, scope]);
  const clear = useCallback((key = scope) => setLists(all => {
    (all[key] || []).forEach(item => URL.revokeObjectURL(item.preview));
    const { [key]: dropped, ...rest } = all;
    return rest;
  }), [scope]);
  const reset = useCallback(() => setLists(all => { Object.values(all).flat().forEach(item => URL.revokeObjectURL(item.preview)); return {}; }), []);
  useEffect(() => () => { Object.values(listsRef.current).flat().forEach(item => URL.revokeObjectURL(item.preview)); clearTimeout(noticeTimer.current); }, []);

  // What a request carries, and what the thread shows before the server's copy arrives.
  const ready = key => (listsRef.current[key] || []).filter(item => item.status === 'ready');
  const payload = useCallback(key => ready(key).map(item => ({ asset_id: item.asset.id, role: item.role })), []);
  const views = useCallback(key => ready(key).map(item => ({ asset_id: item.asset.id, role: item.role, url: item.asset.url, width: item.asset.width, height: item.asset.height })), []);

  const uploading = items.some(item => item.status === 'uploading');
  const failed = items.some(item => item.status === 'failed');
  return useMemo(() => ({
    enabled: !!enabled && !!upload, items, limit: ATTACHMENT_LIMIT, full: items.length >= ATTACHMENT_LIMIT, uploading, failed, busy: uploading || failed,
    notice, dismissNotice: () => setNotice(null), add, remove, retry, setRole, clear, reset, payload, views,
  }), [enabled, upload, items, uploading, failed, notice, add, remove, retry, setRole, clear, reset, payload, views]);
}
